/**
 * WhiteboardBoardsPage
 *
 * Find a previously-imported whiteboard later and see what's in it. Lists the
 * saved boards (whiteboardBaselineStore) and, for the selected one, shows its
 * items and the relationships drawn between them, with a spatial mini-map and a
 * one-click "Re-capture" into the v1→v2 update flow.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Camera, History, ArrowRight, ArrowLeftRight, Loader2, Layers, RefreshCw, MonitorPlay, Image, LayoutGrid, Sparkles, ListOrdered } from 'lucide-react';
import { loadBoards, saveBoardBaseline, type WhiteboardBoard } from '../../lib/whiteboardBaselineStore';
import { getBoardCapture } from '../../lib/whiteboardCaptureStore';
import type { BaselineItem } from '../../lib/whiteboardMerge';
import { planBoardEdits, type BoardEdit } from '../../lib/whiteboardProjectionEdit';
import { buildRevealScriptFromBaseline } from '../../lib/whiteboardReveal';
import type { SessionPrefill } from '../../lib/sessionPrefill';
import { FEATURE_FLAGS, useFeatureFlag } from '../../lib/featureFlags';
import { WhiteboardImportModal } from '../../components/prioritization/item-list/WhiteboardImportModal';
import { WhiteboardRevealOverlay } from '../../components/prioritization/item-list/reveal/WhiteboardRevealOverlay';
import { WhiteboardProjectionView, type WhiteboardProjectionArtifact } from './WhiteboardProjectionView';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useUIStore } from '../../stores/prioritization/uiStore';

const REL_LABEL: Record<string, string> = {
  dependency: 'depends on',
  prerequisite: 'needs',
  related: 'related to',
  contains: 'contains',
  breakdown: 'breaks down into',
  elaboration: 'elaborates',
  aggregation: 'aggregates',
};

function SpatialMap({ items }: { items: BaselineItem[] }) {
  if (items.length === 0) return null;
  return (
    <div className="relative w-full h-44 border border-[var(--rule)] bg-[var(--surface)] overflow-hidden">
      <span className="absolute top-1 left-2 text-[10px] uppercase tracking-wide text-[var(--text-ter)]">Strategic ↑</span>
      <span className="absolute bottom-1 left-2 text-[10px] uppercase tracking-wide text-[var(--text-ter)]">Tactical ↓</span>
      {items.map((it) => (
        <div
          key={it.id}
          title={it.title}
          className="absolute -translate-x-1/2 -translate-y-1/2 bg-primary-400 border border-primary-600/40 shadow-sm"
          style={{ left: `${it.position.x * 100}%`, top: `${it.position.y * 100}%`, width: 12, height: 9 }}
        />
      ))}
    </div>
  );
}

/**
 * The attributes an import assigned to an item — category, altitude, and any
 * visual-language values (e.g. impact = 10). Read live so edits since import
 * show through. Renders nothing when the item carries no such attributes.
 */
function AttrChips({ item }: { item?: { itemCategory?: string; altitudeLevel?: number; metadata?: Record<string, unknown> } }) {
  if (!item) return null;
  const chips: Array<{ key: string; label: string; cls: string }> = [];
  if (item.itemCategory) chips.push({ key: 'cat', label: item.itemCategory, cls: 'bg-primary-50 text-primary-700' });
  if (item.altitudeLevel) chips.push({ key: 'alt', label: `alt ${item.altitudeLevel}`, cls: 'bg-[var(--hover-tint)] text-[var(--text-sec)]' });
  for (const [k, v] of Object.entries(item.metadata ?? {})) {
    if (!k.toLowerCase().startsWith('attribute:')) continue;
    if (v == null || typeof v === 'object') continue;
    chips.push({ key: k, label: `${k.replace(/^attribute:/i, '')}: ${String(v)}`, cls: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' });
  }
  if (chips.length === 0) return null;
  return (
    <span className="flex flex-wrap gap-1">
      {chips.map((c) => (
        <span key={c.key} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${c.cls}`}>{c.label}</span>
      ))}
    </span>
  );
}

export function WhiteboardBoardsPage() {
  const liveItems = useItemsStore((s) => s.items);
  const updateItem = useItemsStore((s) => s.updateItem);
  const addNotification = useUIStore((s) => s.addNotification);
  const [boards, setBoards] = useState<WhiteboardBoard[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [recaptureOpen, setRecaptureOpen] = useState(false);
  const [projectOpen, setProjectOpen] = useState(false);
  const [revealOpen, setRevealOpen] = useState(false);
  const revealEnabled = useFeatureFlag(FEATURE_FLAGS.WHITEBOARD_REVEAL);
  const [projectionArtifact, setProjectionArtifact] = useState<WhiteboardProjectionArtifact>('structured');
  const [captureImages, setCaptureImages] = useState<string[] | null>(null);
  const [captureLoading, setCaptureLoading] = useState(false);

  const refresh = () => {
    setLoading(true);
    loadBoards().then((b) => {
      setBoards(b);
      setLoading(false);
      setSelectedId((cur) => cur ?? b[0]?.boardId ?? null);
    });
  };
  useEffect(refresh, []);

  const selected = boards.find((b) => b.boardId === selectedId) ?? null;

  useEffect(() => {
    if (!selected) {
      setCaptureImages(null);
      setCaptureLoading(false);
      return;
    }
    if (selected.captureImageUrls?.length) {
      setCaptureImages(selected.captureImageUrls);
      setCaptureLoading(false);
      return;
    }
    let active = true;
    setCaptureLoading(true);
    getBoardCapture(selected.boardId).then((rec) => {
      if (!active) return;
      const images = rec?.images ?? null;
      setCaptureImages(images);
      setCaptureLoading(false);
      if (!images?.length) {
        setProjectionArtifact((cur) => (cur === 'original' ? 'structured' : cur));
      }
    });
    return () => { active = false; };
  }, [selected, selected?.boardId, selected?.versionNumber, selected?.captureImageUrls]);

  // Direct-edit save (rung 3): the projection view hands back the moves/deletes
  // made on the projected surface. We update the live items (altitude / archive)
  // and persist the next board version, preserving every identity.
  const handleSaveEdits = async (board: WhiteboardBoard, edits: BoardEdit[]) => {
    const plan = planBoardEdits(board.baseline, edits);
    for (const m of plan.moved) {
      try {
        updateItem(m.id, { altitudeLevel: m.altitudeLevel } as Partial<Parameters<typeof updateItem>[1]>);
      } catch { /* continue */ }
    }
    for (const id of plan.removedItemIds) {
      try {
        updateItem(id, { exit_status: 'archived' } as Partial<Parameters<typeof updateItem>[1]>);
      } catch { /* continue */ }
    }
    try {
      await saveBoardBaseline(board.boardId, board.title, plan.nextBaseline);
    } catch { /* baseline save is best-effort */ }
    addNotification({
      type: 'success',
      title: `“${board.title}” updated to v${board.versionNumber + 1}`,
      message: `${plan.summary.moved} moved, ${plan.summary.removed} removed directly on the board.`,
      duration: 4000,
    });
    refresh();
  };

  // Index the live items so the board view can show each one's current title and
  // the attributes the import assigned (category, altitude, visual-language values).
  const liveById = useMemo(() => {
    const map = new Map<string, (typeof liveItems)[number]>();
    for (const it of liveItems) if (it.id) map.set(it.id, it);
    return map;
  }, [liveItems]);
  const titleOf = (id: string, fallback: string) => liveById.get(id)?.title ?? fallback;
  const itemExists = (id: string) => liveById.has(id);

  // The wall-to-plan seam (SPEC-04): rank a saved board's items in a session.
  // Only items that still exist in the workspace carry over, with live titles.
  const navigate = useNavigate();
  const rankBoardItems = (board: WhiteboardBoard) => {
    const items = board.baseline.items
      .filter((it) => itemExists(it.id))
      .map((it) => ({ id: it.id, title: titleOf(it.id, it.title) }));
    const prefill: SessionPrefill = {
      ask: `Rank the priorities from “${board.title}”`,
      items,
      origin: 'whiteboard_board',
    };
    navigate('/prioritization/sessions/new', { state: { prefill } });
  };

  // Replay the import reveal for a saved board — the same overlay the import
  // ends in, rebuilt from the durable baseline plus live item attributes.
  const revealScript = useMemo(
    () =>
      revealOpen && selected
        ? buildRevealScriptFromBaseline(
            selected,
            {
              categoryOf: (id) => liveById.get(id)?.itemCategory,
              altitudeOf: (id) => liveById.get(id)?.altitudeLevel,
            },
            { photoUrl: captureImages?.[0] },
          )
        : null,
    [revealOpen, selected, liveById, captureImages],
  );

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-50"><Camera className="h-5 w-5 text-primary-600" /></div>
          <div>
            <h1 className="text-xl font-semibold text-[var(--text-pri)]">Whiteboards</h1>
            <p className="text-sm text-[var(--text-ter)]">Boards you've imported from photos — their items and relationships.</p>
          </div>
        </div>
        <button onClick={refresh} className="btn btn-secondary flex items-center gap-2 text-sm" title="Refresh">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[var(--text-ter)] py-16 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading boards…
        </div>
      ) : boards.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-16 text-center text-[var(--text-ter)]">
          <Layers className="h-10 w-10 text-[var(--text-ter)]" />
          <p className="text-base font-medium text-[var(--text-sec)]">No whiteboards yet</p>
          <p className="text-sm max-w-sm">
            Go to <Link to="/prioritization/items" className="text-primary-600 underline">Items → Import → Whiteboard Photo</Link> to
            capture a physical whiteboard. It's saved here as v1, and you can re-photograph it later to capture changes.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-5">
          {/* Board list */}
          <div className="space-y-2">
            {boards.map((b) => (
              <button
                key={b.boardId}
                onClick={() => setSelectedId(b.boardId)}
                className={`w-full text-left p-3  border transition-colors ${
                  selectedId === b.boardId ? 'border-primary-400 bg-primary-50/50' : 'border-[var(--rule)] hover:border-primary-300 hover:bg-[var(--surface)]'
                }`}
              >
                <p className="text-sm font-medium text-[var(--text-pri)] truncate">{b.title}</p>
                <p className="text-xs text-[var(--text-ter)] mt-0.5">
                  v{b.versionNumber} · {b.baseline.items.length} items · {b.baseline.relationships.length} links
                </p>
                <p className="text-[11px] text-[var(--text-ter)] mt-0.5">Updated {new Date(b.updatedAt).toLocaleDateString()}</p>
              </button>
            ))}
          </div>

          {/* Detail */}
          {selected && (
            <div className="space-y-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-[var(--text-pri)]">{selected.title}</h2>
                  <p className="text-xs text-[var(--text-ter)]">
                    Version {selected.versionNumber} · captured {new Date(selected.baseline.capturedAt).toLocaleString()}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  <div className="flex items-center gap-2">
                    {revealEnabled && (
                      <button
                        onClick={() => setRevealOpen(true)}
                        className="btn btn-secondary flex items-center gap-2 text-sm"
                        title="Replay this board's import reveal"
                      >
                        <Sparkles className="h-4 w-4" /> Reveal
                      </button>
                    )}
                    {revealEnabled && selected.baseline.items.some((it) => itemExists(it.id)) && (
                      <button
                        onClick={() => rankBoardItems(selected)}
                        className="btn btn-secondary flex items-center gap-2 text-sm"
                        title="Open the session wizard with this board's items pre-selected"
                      >
                        <ListOrdered className="h-4 w-4" /> Rank these
                      </button>
                    )}
                    <button onClick={() => setProjectOpen(true)} className="btn btn-secondary flex items-center gap-2 text-sm">
                      <MonitorPlay className="h-4 w-4" /> Project
                    </button>
                    <button onClick={() => setRecaptureOpen(true)} className="btn btn-primary flex items-center gap-2 text-sm">
                      <History className="h-4 w-4" /> Re-capture
                    </button>
                  </div>
                  <div className="inline-flex items-center border border-[var(--rule)] bg-[var(--surface)] p-0.5 text-xs">
                    <span className="px-2 text-[var(--text-ter)]">Project as</span>
                    <button
                      type="button"
                      onClick={() => setProjectionArtifact('structured')}
                      className={`inline-flex items-center gap-1 px-2 py-1  transition-colors ${
                        projectionArtifact === 'structured' ? 'bg-[var(--bg-raised)] shadow-sm text-[var(--text-pri)]' : 'text-[var(--text-sec)] hover:text-[var(--text-pri)]'
                      }`}
                    >
                      <LayoutGrid className="h-3.5 w-3.5" /> Structured
                    </button>
                    <button
                      type="button"
                      onClick={() => setProjectionArtifact('original')}
                      disabled={captureLoading || !captureImages?.length}
                      title={
                        captureLoading
                          ? 'Loading capture…'
                          : captureImages?.length
                            ? 'Project the original captured photo'
                            : 'No capture photo yet — import or re-capture to save one'
                      }
                      className={`inline-flex items-center gap-1 px-2 py-1  transition-colors ${
                        projectionArtifact === 'original'
                          ? 'bg-[var(--bg-raised)] shadow-sm text-[var(--text-pri)]'
                          : captureImages?.length
                            ? 'text-[var(--text-sec)] hover:text-[var(--text-pri)]'
                            : 'text-[var(--text-ter)] cursor-not-allowed'
                      }`}
                    >
                      <Image className="h-3.5 w-3.5" /> Original photo
                    </button>
                  </div>
                </div>
              </div>

              <SpatialMap items={selected.baseline.items} />

              {/* Items */}
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-2">
                  Items ({selected.baseline.items.length})
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {selected.baseline.items.map((it) => (
                    itemExists(it.id) ? (
                      <Link
                        key={it.id}
                        to={`/prioritization/items/${it.id}`}
                        className="flex flex-col gap-1 px-3 py-2 border border-[var(--rule)] bg-[var(--bg-raised)] hover:border-primary-300 hover:bg-primary-50/40 text-sm text-[var(--text-pri)]"
                      >
                        <span className="flex items-center gap-2">
                          {it.code && (
                            <span className="px-1.5 py-0.5 rounded bg-[var(--hover-tint)] text-[var(--text-sec)] font-mono text-[11px] flex-shrink-0">{it.code}</span>
                          )}
                          <span className="truncate">{titleOf(it.id, it.title)}</span>
                        </span>
                        <AttrChips item={liveById.get(it.id)} />
                      </Link>
                    ) : (
                      <span
                        key={it.id}
                        title="No longer in the items library"
                        className="flex items-center gap-2 px-3 py-2 border border-dashed border-[var(--rule)] bg-[var(--surface)] text-sm text-[var(--text-ter)]"
                      >
                        {it.code && <span className="px-1.5 py-0.5 rounded bg-[var(--hover-tint)] font-mono text-[11px] flex-shrink-0">{it.code}</span>}
                        <span className="truncate line-through">{it.title}</span>
                      </span>
                    )
                  ))}
                </div>
                {selected.baseline.items.some((it) => it.code) && (
                  <p className="text-xs text-[var(--text-ter)] mt-2">
                    Write each code in a corner of its box on the board — re-capture then matches them exactly, even after a rename or move.
                  </p>
                )}
              </div>

              {/* Relationships */}
              {selected.baseline.relationships.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-2">
                    Relationships ({selected.baseline.relationships.length})
                  </h3>
                  <div className="space-y-1.5">
                    {selected.baseline.relationships.map((r, i) => {
                      const itemTitle = (id: string) =>
                        titleOf(id, selected.baseline.items.find((it) => it.id === id)?.title ?? id.slice(0, 6));
                      return (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <span className="text-[var(--text-pri)] truncate max-w-[180px]">{itemTitle(r.sourceItemId)}</span>
                          {r.directionality === 'directed'
                            ? <ArrowRight className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
                            : <ArrowLeftRight className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />}
                          <span className="text-[var(--text-pri)] truncate max-w-[180px]">{itemTitle(r.targetItemId)}</span>
                          <span className="ml-auto text-xs text-[var(--text-ter)] italic">{REL_LABEL[r.type] ?? r.type}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {recaptureOpen && selected && (
        <WhiteboardImportModal
          initialMode="recapture"
          recaptureBoardId={selected.boardId}
          onClose={() => { setRecaptureOpen(false); refresh(); }}
          onImported={() => refresh()}
        />
      )}

      {revealOpen && revealScript && (
        <WhiteboardRevealOverlay
          script={revealScript}
          mode="auto"
          origin="whiteboard_board"
          onClose={() => setRevealOpen(false)}
        />
      )}

      {projectOpen && selected && (
        <WhiteboardProjectionView
          baseline={selected.baseline}
          title={selected.title}
          captureImages={captureImages ?? undefined}
          initialArtifact={projectionArtifact}
          onArtifactChange={setProjectionArtifact}
          onClose={() => setProjectOpen(false)}
          onSaveEdits={(edits) => handleSaveEdits(selected, edits)}
        />
      )}
    </div>
  );
}

export default WhiteboardBoardsPage;
